import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    getNotifications,
    getUnreadNotificationsCount,
    markAllNotificationsAsRead,
    markNotificationAsRead
} from "../services/notifications-services";
import { TApiResponse, TErrorMessage, TNotification, TPager, TSuccess } from "@/types";
import toast from "react-hot-toast";

export const QUERY_KEY = "notifications";
export const UNREAD_COUNT_QUERY_KEY = "notifications-unread-count";


export function useGetNotifications(options?: { pager?: TPager }) {
    const { isLoading, isFetching, isError, data } = useQuery({
        queryKey: [QUERY_KEY, { ...options?.pager }],
        queryFn: () => getNotifications(options?.pager),
    });

    return { isLoading, isFetching, isError, notifications: data }
}

export function useGetUnreadNotificationsCount() {
    const { isLoading, isFetching, data } = useQuery({
        queryKey: [UNREAD_COUNT_QUERY_KEY],
        queryFn: () => getUnreadNotificationsCount(),
    });

    return { isLoading, isFetching, unreadCount: data?.results ?? 0 }
}

export function useMarkNotificationAsRead(
    onSuccess?: TSuccess<TApiResponse<null>>
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: TNotification["id"]) => markNotificationAsRead(id),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_QUERY_KEY] });
            onSuccess?.(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "");
        },
    })
}

export function useMarkAllNotificationsAsRead(
    onSuccess?: TSuccess<TApiResponse<null>>
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => markAllNotificationsAsRead(),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_QUERY_KEY] });
            onSuccess?.(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "");
        },
    })
}
